/**
 * Which delivery zone a postcode lands in, for a customer tier.
 *
 * Mirrors pickDeliveryZone / zoneMatchesPostcode in
 * HHNodeServer/src/services/nurseryService.js, which stays authoritative — it
 * is what prices an order. This copy only powers the live preview on the
 * customer form, so whoever types a postcode sees at once whether the nursery
 * delivers there and at what rate.
 */

import type { NurseryDeliveryZone } from '../types/nursery';

export function zoneMatchesPostcode(zone: NurseryDeliveryZone, postcode: string | null | undefined): boolean {
  if (zone.match_type === 'all') return true;
  const pc = (postcode ?? '').trim();
  if (!pc) return false;
  const values = (zone.match_value || '').split(',').map((v) => v.trim()).filter(Boolean);
  if (zone.match_type === 'postcode_list') return values.includes(pc);
  if (zone.match_type === 'postcode_prefix') return values.some((p) => pc.startsWith(p));
  return false;
}

const specificity = (z: NurseryDeliveryZone) =>
  z.match_type === 'postcode_list' ? 3 : z.match_type === 'postcode_prefix' ? 2 : 1;

/**
 * Tier-specific beats all-tier, then the more specific rule, then the lower
 * fee, then sort order. Inactive zones and zones for another tier never match.
 */
export function pickDeliveryZone(
  zones: NurseryDeliveryZone[],
  tierId: number | null | undefined,
  postcode: string | null | undefined,
): NurseryDeliveryZone | null {
  const tier = tierId ?? null;
  const matches = zones.filter(
    (z) => z.active
      && (z.customer_tier_id == null || z.customer_tier_id === tier)
      && zoneMatchesPostcode(z, postcode),
  );
  if (matches.length === 0) return null;
  matches.sort((a, b) => {
    const at = a.customer_tier_id === tier ? 1 : 0;
    const bt = b.customer_tier_id === tier ? 1 : 0;
    if (bt !== at) return bt - at;
    const sp = specificity(b) - specificity(a);
    if (sp !== 0) return sp;
    const fee = Number(a.flat_fee) - Number(b.flat_fee);
    if (fee !== 0) return fee;
    return a.sort_order - b.sort_order;
  });
  return matches[0];
}
